import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Folder, File, Trash } from "lucide-react";

interface FileItem {
  name: string;
  type: "file";
  hash: string;
  collection: string;
}

interface FolderItem {
  name: string;
  type: "folder";
  onClick: () => void;
}

type ListItem = FileItem | FolderItem;

interface FileListProps {
  items: ListItem[];
  onDelete: (name: string) => void;
  viewMode: "list" | "grid";
}

export function FileList({ items, onDelete, viewMode }: FileListProps) {
  const handleDelete = (e: React.MouseEvent, item: ListItem) => {
    e.stopPropagation();
    onDelete(item.name);
  };

  if (viewMode === "list") {
    return (
      <Box>
        {items.map((item) => (
          <Box
            key={item.name}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={1}
            onClick={item.type === "folder" ? item.onClick : undefined}
            sx={{
              borderBottom: "1px solid #3f3f46",
              cursor: item.type === "folder" ? "pointer" : "default",
              "&:hover": { bgcolor: "#27272a" },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {item.type === "folder" ? (
                <Folder size={20} className="text-green-500" />
              ) : (
                <File size={20} className="text-blue-500" />
              )}
              <Typography color="white">{item.name}</Typography>
            </Box>
            <Trash
              size={16}
              className="text-red-500 cursor-pointer hover:text-red-600"
              onClick={(e) => handleDelete(e, item)}
            />
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
        gap: 2,
        p: 1,
      }}
    >
      {items.map((item) => (
        <Box
          key={item.name}
          onClick={item.type === "folder" ? item.onClick : undefined}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            cursor: item.type === "folder" ? "pointer" : "default",
            borderRadius: "8px",
            position: "relative",
            height: "100px",
            "&:hover": {
              bgcolor: "#27272a",
              "& .delete-icon": {
                opacity: 1,
              },
            },
          }}
        >
          {item.type === "folder" ? (
            <Folder size={40} className="text-green-500" />
          ) : (
            <File size={40} className="text-blue-500" />
          )}
          <Typography
            color="white"
            sx={{
              fontSize: "0.75rem",
              mt: 1,
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              width: "100%",
            }}
          >
            {item.name}
          </Typography>
          <Box
            className="delete-icon"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              opacity: 0,
              transition: "opacity 0.2s",
              backgroundColor: "#27272a",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trash
              size={14}
              className="text-red-500 cursor-pointer hover:text-red-600"
              onClick={(e) => handleDelete(e, item)}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default FileList;
